import type { GameState, SeasonState } from "../types";
import { parseGameState } from "./schema";

// v2: synergy reasons/tags became structured objects (was string[]) for i18n,
// so an in-flight v1 season would render stale synergy text — start clean.
// v3: Driver gained a `rain` field; an in-flight v2 season's saved drivers
// would lack it (NaN scores, "RAN undefined" in the UI) — start clean again.
// v4: SynergyBonus dropped pace/aero (historic-drive bonus became per-driver);
// an in-flight v3 season would carry the stale shared bonus — start clean.
// v5: the draft gained a mandatory 4th slot (Director) plus Slots.director,
// SeasonState.directorPack and Team.director; a v4 season would lack them — a
// fresh key cleanly discards any in-flight v4 draft rather than migrating it.
// v6: driver variants are keyed by team (Driver dropped `season`); an in-flight
// v5 season's stored driver cards carry the old shape — start clean.
const KEY = "f1dl_ts_v6";

export function freshState(): GameState {
  return { phase: "menu", season: null, history: [], unlockedBadges: [], teamName: "" };
}

export function loadState(): GameState {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return freshState();
    const parsed = parseGameState(JSON.parse(raw));
    if (!parsed) return freshState();
    // If a season was mid-flight and its shape doesn't match the current build,
    // keep history but reset to the menu rather than risk a broken render.
    if (parsed.phase !== "menu" && !isUsableSeason(parsed.season)) {
      return {
        phase: "menu",
        season: null,
        history: parsed.history,
        unlockedBadges: parsed.unlockedBadges,
        teamName: parsed.teamName,
      };
    }
    return parsed;
  } catch {
    return freshState();
  }
}

function isUsableSeason(season: SeasonState | null): boolean {
  if (!season || typeof season !== "object") return false;
  const s = season as unknown as Record<string, unknown>;
  return Array.isArray(s["pack"]) && Array.isArray(s["picked"]) && Array.isArray(s["opponents"]);
}

export function saveState(state: GameState): void {
  try {
    localStorage.setItem(KEY, JSON.stringify(state));
  } catch {
    // storage full or unavailable — non-fatal
  }
}

export function clearState(): void {
  try {
    localStorage.removeItem(KEY);
  } catch {
    /* ignore */
  }
}
